import React, { useEffect, useState, useCallback, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import { Tooltip, Popover, Input, Button, Space } from "antd";
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaStrikethrough,
  FaListUl,
  FaListOl,
  FaQuoteLeft,
  FaLink,
  FaUnlink,
  FaUndo,
  FaRedo,
  FaCode,
  FaEraser,
  FaMinus,
  FaTerminal
} from "react-icons/fa";

/**
 * Utility to convert legacy raw or delimited text formats into standard HTML paragraphs.
 * Handles legacy formats like '/n', '/n,', and '*' bullets.
 */
export const convertLegacyContent = (raw) => {
  if (raw === null || raw === undefined || typeof raw === "object") {
    return "";
  }
  let safe = String(raw).trim();
  if (!safe) return "";

  // If content already contains HTML block tags, return as-is
  if (
    safe.includes("<p") ||
    safe.includes("<div") ||
    safe.includes("<li") ||
    safe.includes("<h1") ||
    safe.includes("<h2") ||
    safe.includes("<h3") ||
    safe.includes("<h4") ||
    safe.includes("<ul") ||
    safe.includes("<ol") ||
    safe.includes("<blockquote") ||
    safe.includes("<pre") ||
    safe.includes("<hr")
  ) {
    return safe;
  }

  // Convert legacy line delimiters
  if (safe.includes("/n") || safe.includes("*")) {
    const list = safe.includes("/n")
      ? safe.includes("/n,")
        ? safe.split("/n,")
        : safe.split("/n")
      : safe.split("*");

    const validLines = list.map((line) => line.trim()).filter(Boolean);
    if (validLines.length > 0) {
      return validLines.map((line) => `<p>${line}</p>`).join("");
    }
  }

  // Standard plain text to paragraph
  return `<p>${safe}</p>`;
};

// Toolbar Button Component
const ToolbarButton = ({
  onClick,
  active = false,
  disabled = false,
  title,
  children,
  ariaLabel,
  style = {}
}) => (
  <Tooltip title={title} mouseEnterDelay={0.3}>
    <button
      type="button"
      className={`tiptap-btn ${active ? "active" : ""}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onClick) onClick();
      }}
      disabled={disabled}
      aria-label={ariaLabel || title}
      style={style}
    >
      {children}
    </button>
  </Tooltip>
);

const ToolbarDivider = () => <div className="tiptap-divider" />;

const TipTapEditor = ({
  description,
  value,
  onChange,
  height = "350px",
  minHeight = "150px",
  placeholder = "Write content here...",
  editable = true,
  disabled = false,
  className = "",
  style = {}
}) => {
  const isEffectivelyEditable = editable && !disabled;
  const contentProp = value !== undefined ? value : (description || "");
  const [isFocused, setIsFocused] = useState(false);
  const [linkInputVisible, setLinkInputVisible] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const isUpdatingFromPropRef = useRef(false);
  const savedSelectionRef = useRef(null);

  // Initialize TipTap
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
        heading: {
          levels: [1, 2, 3]
        }
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
        HTMLAttributes: {
          target: "_blank",
          rel: "noopener noreferrer",
          class: "tiptap-link"
        }
      })
    ],
    content: convertLegacyContent(contentProp),
    editable: isEffectivelyEditable,
    onUpdate: ({ editor: currentEditor }) => {
      if (isUpdatingFromPropRef.current) return;
      const html = currentEditor.isEmpty ? "" : currentEditor.getHTML();
      if (onChange) {
        onChange(html);
      }
    },
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false)
  });

  // Keep editable state synchronized
  useEffect(() => {
    if (!editor) return;
    if (editor.isEditable !== isEffectivelyEditable) {
      editor.setEditable(isEffectivelyEditable);
    }
  }, [editor, isEffectivelyEditable]);

  // Keep editor content in sync with external value/description prop without clobbering active edits
  useEffect(() => {
    if (!editor) return;
    // CRITICAL: Do NOT override editor if the user is actively focused in the editor
    if (editor.isFocused) return;

    const convertedProp = convertLegacyContent(contentProp);
    const currentHTML = editor.getHTML();

    const isPropEmpty =
      !convertedProp || convertedProp === "<p></p>" || convertedProp === "";
    const isEditorEmpty =
      editor.isEmpty || currentHTML === "<p></p>" || currentHTML === "";

    if (isPropEmpty && isEditorEmpty) return;

    if (convertedProp !== currentHTML) {
      isUpdatingFromPropRef.current = true;
      editor.commands.setContent(convertedProp || "", false);
      isUpdatingFromPropRef.current = false;
    }
  }, [contentProp, editor]);

  // Handle Opening Link Popover & Preserving Selection
  const handleOpenLinkPopover = () => {
    if (!editor) return;
    savedSelectionRef.current = editor.state.selection;
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, " ");
    
    const previousUrl = editor.getAttributes("link").href || "";
    setLinkUrl(previousUrl);
    setLinkText(selectedText || "");
    setLinkInputVisible(true);
  };

  // Handle Link Submission
  const handleApplyLink = useCallback(() => {
    if (!editor) return;

    let formattedUrl = linkUrl.trim();
    if (formattedUrl && !/^https?:\/\//i.test(formattedUrl) && !formattedUrl.startsWith("mailto:")) {
      formattedUrl = `https://${formattedUrl}`;
    }

    // Restore saved selection
    if (savedSelectionRef.current) {
      editor.commands.setTextSelection(savedSelectionRef.current);
    }
    editor.commands.focus();

    if (!formattedUrl) {
      editor.chain().focus().unsetLink().run();
      setLinkInputVisible(false);
      return;
    }

    const { from, to } = editor.state.selection;
    const hasSelection = from !== to;

    if (hasSelection) {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: formattedUrl })
        .run();
    } else {
      // If no text was highlighted, insert linked text
      const displayText = linkText.trim() || formattedUrl;
      editor
        .chain()
        .focus()
        .insertContent(`<a href="${formattedUrl}">${displayText}</a> `)
        .run();
    }

    setLinkInputVisible(false);
    setLinkUrl("");
    setLinkText("");
    savedSelectionRef.current = null;
  }, [editor, linkUrl, linkText]);

  const handleRemoveLink = () => {
    if (!editor) return;
    if (savedSelectionRef.current) {
      editor.commands.setTextSelection(savedSelectionRef.current);
    }
    editor.chain().focus().unsetLink().run();
    setLinkInputVisible(false);
    setLinkUrl("");
    setLinkText("");
    savedSelectionRef.current = null;
  };

  if (!editor) {
    return null;
  }

  const linkPopoverContent = (
    <div style={{ width: 280, padding: "4px 0" }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: "#334155" }}>
        Insert / Edit Link
      </div>
      <Input
        size="small"
        placeholder="https://example.com"
        value={linkUrl}
        onChange={(e) => setLinkUrl(e.target.value)}
        onPressEnter={handleApplyLink}
        autoFocus
        style={{ marginBottom: 6 }}
      />
      <Input
        size="small"
        placeholder="Display Text (optional)"
        value={linkText}
        onChange={(e) => setLinkText(e.target.value)}
        onPressEnter={handleApplyLink}
        style={{ marginBottom: 8 }}
      />
      <div className="d-flex justify-content-between align-items-center">
        {editor.isActive("link") ? (
          <Button
            size="small"
            danger
            type="text"
            icon={<FaUnlink style={{ fontSize: 11 }} />}
            onClick={handleRemoveLink}
          >
            Remove
          </Button>
        ) : (
          <span />
        )}
        <Space size={6}>
          <Button size="small" onClick={() => setLinkInputVisible(false)}>
            Cancel
          </Button>
          <Button size="small" type="primary" onClick={handleApplyLink}>
            Apply
          </Button>
        </Space>
      </div>
    </div>
  );

  return (
    <div
      className={`tiptap-editor-container ${isFocused ? "focused" : ""} ${!isEffectivelyEditable ? "disabled" : ""} ${className}`}
      style={{
        height: height || "350px",
        minHeight: minHeight || "150px",
        ...style
      }}
    >
      <style>{`
        .tiptap-editor-container {
          display: flex;
          flex-direction: column;
          border: 1px solid #d9d9d9;
          border-radius: 8px;
          background-color: #ffffff;
          overflow: hidden;
          transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1), box-shadow 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
        }
        .tiptap-editor-container.focused {
          border-color: #1677ff;
          box-shadow: 0 0 0 2px rgba(5, 145, 255, 0.1);
        }
        .tiptap-editor-container.disabled {
          background-color: #f8fafc;
          border-color: #e2e8f0;
          cursor: not-allowed;
        }
        .tiptap-editor-container.disabled .tiptap-toolbar {
          background: #f1f5f9;
          opacity: 0.6;
          pointer-events: none;
        }
        .tiptap-editor-container.disabled .tiptap-content-wrapper {
          background: #f8fafc;
          cursor: not-allowed;
        }
        .tiptap-toolbar {
          flex-shrink: 0;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 3px;
          padding: 6px 10px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          user-select: none;
        }
        .tiptap-btn {
          height: 28px;
          min-width: 28px;
          padding: 0 7px;
          border-radius: 5px;
          border: 1px solid transparent;
          background: transparent;
          color: #475569;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          line-height: 1;
          transition: all 0.15s ease;
        }
        .tiptap-btn:hover:not(:disabled) {
          background: #e2e8f0;
          color: #0f172a;
        }
        .tiptap-btn.active {
          background: #e6f4ff;
          color: #1677ff;
          border-color: #91caff;
          font-weight: 700;
        }
        .tiptap-btn:disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }
        .tiptap-btn-text {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.02em;
        }
        .tiptap-divider {
          width: 1px;
          height: 16px;
          background: #cbd5e1;
          margin: 0 4px;
          flex-shrink: 0;
        }
        .tiptap-content-wrapper {
          flex: 1;
          overflow-y: auto;
          background: #ffffff;
          cursor: text;
        }
        .tiptap-content-wrapper .ProseMirror {
          min-height: 100%;
          padding: 12px 16px;
          outline: none;
          font-size: 14px;
          line-height: 1.65;
          color: #1e293b;
        }
        .tiptap-content-wrapper .ProseMirror h1 {
          font-size: 20px;
          font-weight: 800;
          color: #0f172a;
          margin: 14px 0 6px 0;
        }
        .tiptap-content-wrapper .ProseMirror h2 {
          font-size: 17px;
          font-weight: 700;
          color: #0f172a;
          margin: 12px 0 4px 0;
        }
        .tiptap-content-wrapper .ProseMirror h3 {
          font-size: 15px;
          font-weight: 600;
          color: #1e293b;
          margin: 10px 0 4px 0;
        }
        .tiptap-content-wrapper .ProseMirror p {
          margin: 0 0 8px 0;
        }
        .tiptap-content-wrapper .ProseMirror p:last-child {
          margin-bottom: 0;
        }
        .tiptap-content-wrapper .ProseMirror ul {
          list-style: disc !important;
          list-style-type: disc !important;
          padding-left: 24px !important;
          margin: 8px 0 !important;
        }
        .tiptap-content-wrapper .ProseMirror ol {
          list-style: decimal !important;
          list-style-type: decimal !important;
          padding-left: 24px !important;
          margin: 8px 0 !important;
        }
        .tiptap-content-wrapper .ProseMirror li {
          display: list-item !important;
          list-style: inherit !important;
          margin: 3px 0 !important;
        }
        .tiptap-content-wrapper .ProseMirror li p {
          margin: 0 !important;
          display: inline !important;
        }
        .tiptap-content-wrapper .ProseMirror blockquote {
          margin: 8px 0;
          padding: 6px 14px;
          border-left: 3px solid #1677ff;
          background: #f8fafc;
          color: #475569;
          font-style: italic;
          border-radius: 0 4px 4px 0;
        }
        .tiptap-content-wrapper .ProseMirror code {
          background: #f1f5f9;
          color: #0f172a;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-size: 13px;
          border: 1px solid #e2e8f0;
        }
        .tiptap-content-wrapper .ProseMirror pre {
          background: #0f172a;
          color: #f8fafc;
          padding: 10px 14px;
          border-radius: 6px;
          font-size: 13px;
          margin: 8px 0;
          overflow-x: auto;
        }
        .tiptap-content-wrapper .ProseMirror pre code {
          background: transparent;
          color: inherit;
          padding: 0;
          border: none;
        }
        .tiptap-content-wrapper .ProseMirror hr {
          border: none;
          border-top: 2px solid #e2e8f0;
          margin: 16px 0;
        }
        .tiptap-content-wrapper .ProseMirror a.tiptap-link {
          color: #1677ff;
          text-decoration: underline;
          cursor: pointer;
        }
        .tiptap-content-wrapper .ProseMirror a.tiptap-link:hover {
          color: #0958d9;
        }
      `}</style>

      {/* Modern Sleek Toolbar */}
      <div className="tiptap-toolbar">
        {/* Undo / Redo */}
        <ToolbarButton
          title="Undo (Ctrl+Z)"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
        >
          <FaUndo style={{ fontSize: 11 }} />
        </ToolbarButton>
        <ToolbarButton
          title="Redo (Ctrl+Y)"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
        >
          <FaRedo style={{ fontSize: 11 }} />
        </ToolbarButton>

        <ToolbarDivider />

        {/* Headings */}
        <ToolbarButton
          title="Normal Text (Paragraph)"
          active={editor.isActive("paragraph")}
          onClick={() => editor.chain().focus().setParagraph().run()}
        >
          <span className="tiptap-btn-text">P</span>
        </ToolbarButton>
        <ToolbarButton
          title="Heading 1"
          active={editor.isActive("heading", { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        >
          <span className="tiptap-btn-text">H1</span>
        </ToolbarButton>
        <ToolbarButton
          title="Heading 2"
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <span className="tiptap-btn-text">H2</span>
        </ToolbarButton>
        <ToolbarButton
          title="Heading 3"
          active={editor.isActive("heading", { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          <span className="tiptap-btn-text">H3</span>
        </ToolbarButton>

        <ToolbarDivider />

        {/* Formatting */}
        <ToolbarButton
          title="Bold (Ctrl+B)"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <FaBold style={{ fontSize: 12 }} />
        </ToolbarButton>
        <ToolbarButton
          title="Italic (Ctrl+I)"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <FaItalic style={{ fontSize: 12 }} />
        </ToolbarButton>
        <ToolbarButton
          title="Underline (Ctrl+U)"
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <FaUnderline style={{ fontSize: 12 }} />
        </ToolbarButton>
        <ToolbarButton
          title="Strikethrough"
          active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <FaStrikethrough style={{ fontSize: 12 }} />
        </ToolbarButton>
        <ToolbarButton
          title="Inline Code"
          active={editor.isActive("code")}
          onClick={() => editor.chain().focus().toggleCode().run()}
        >
          <FaCode style={{ fontSize: 12 }} />
        </ToolbarButton>

        <ToolbarDivider />

        {/* Lists & Quotes & Blocks */}
        <ToolbarButton
          title="Bullet List"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <FaListUl style={{ fontSize: 12 }} />
        </ToolbarButton>
        <ToolbarButton
          title="Numbered List"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <FaListOl style={{ fontSize: 12 }} />
        </ToolbarButton>
        <ToolbarButton
          title="Blockquote"
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <FaQuoteLeft style={{ fontSize: 11 }} />
        </ToolbarButton>
        <ToolbarButton
          title="Code Block"
          active={editor.isActive("codeBlock")}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        >
          <FaTerminal style={{ fontSize: 11 }} />
        </ToolbarButton>
        <ToolbarButton
          title="Horizontal Divider"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          <FaMinus style={{ fontSize: 11 }} />
        </ToolbarButton>

        <ToolbarDivider />

        {/* Links */}
        <Popover
          content={linkPopoverContent}
          trigger="click"
          open={linkInputVisible}
          onOpenChange={(visible) => {
            if (visible) handleOpenLinkPopover();
            else setLinkInputVisible(false);
          }}
          placement="bottom"
        >
          <Tooltip title="Insert / Edit Link" mouseEnterDelay={0.3}>
            <button
              type="button"
              className={`tiptap-btn ${editor.isActive("link") ? "active" : ""}`}
              aria-label="Link"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <FaLink style={{ fontSize: 12 }} />
            </button>
          </Tooltip>
        </Popover>

        {editor.isActive("link") && (
          <ToolbarButton
            title="Remove Link"
            onClick={handleRemoveLink}
          >
            <FaUnlink style={{ fontSize: 11 }} />
          </ToolbarButton>
        )}

        {/* Clear Formatting */}
        <ToolbarButton
          title="Clear Formatting"
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        >
          <FaEraser style={{ fontSize: 12 }} />
        </ToolbarButton>
      </div>

      {/* Editor Content Area */}
      <div
        className="tiptap-content-wrapper"
        onClick={() => {
          if (!editor.isFocused) {
            editor.commands.focus();
          }
        }}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default TipTapEditor;
